import { Link, NavLink } from 'react-router-dom';
import { GraduationCapIcon, MenuIcon, PanelLeftOpenIcon } from 'lucide-react';

import { defaultPathForRole, useSession } from '@/components/auth/user-provider';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { ProfileMenu } from '@/components/layout/profile-menu';
import { filterNavigationForRole, topNavigation } from '@/lib/navigation';
import { cn } from '@/lib/utils';

function BrandIdentity() {
  return (
    <>
      <span className="bg-primary text-primary-foreground ring-primary/20 flex size-9 items-center justify-center rounded-xl shadow-sm ring-4">
        <GraduationCapIcon className="size-5" aria-hidden />
      </span>
      <span className="flex flex-col leading-none">
        <span className="text-sm font-bold tracking-tight">School Attendance</span>
        <span className="text-muted-foreground mt-1 text-[10px] font-semibold tracking-[0.16em] uppercase">
          Operations Console
        </span>
      </span>
    </>
  );
}

function useVisibleTopNav() {
  const { session } = useSession();
  const role = session.status === 'signedIn' ? session.user.role : undefined;
  return filterNavigationForRole([{ heading: 'Top', items: topNavigation }], role)[0]?.items ?? [];
}

function useHomePath() {
  const { session } = useSession();
  if (session.status !== 'signedIn') return '/dashboard';
  return defaultPathForRole(session.user.role, session.user.isActive);
}

/** Horizontal top menu in the header. Configured via `topNavigation`. */
function TopNav() {
  const items = useVisibleTopNav();
  if (items.length === 0) return null;

  return (
    <nav
      aria-label="Primary"
      className="border-border/70 bg-secondary/70 text-muted-foreground hidden items-center gap-0.5 rounded-xl border p-1 md:flex"
    >
      {items.map((item) =>
        item.to ? (
          <NavLink
            key={item.title}
            to={item.to}
            end={item.to === '/dashboard'}
            className={({ isActive }) =>
              cn(
                'focus-visible:ring-ring inline-flex items-center rounded-lg px-3 py-1.5 text-sm font-medium transition-all duration-200 focus-visible:ring-2 focus-visible:outline-none motion-reduce:transition-none',
                isActive
                  ? 'bg-primary text-primary-foreground shadow-sm shadow-primary/20'
                  : 'hover:bg-background hover:text-foreground hover:shadow-xs'
              )
            }
          >
            {item.title}
          </NavLink>
        ) : (
          <a
            key={item.title}
            href={item.href}
            target={item.external ? '_blank' : undefined}
            rel={item.external ? 'noreferrer' : undefined}
            className="hover:bg-background hover:text-foreground focus-visible:ring-ring inline-flex items-center rounded-lg px-3 py-1.5 text-sm font-medium transition-all duration-200 focus-visible:ring-2 focus-visible:outline-none motion-reduce:transition-none hover:shadow-xs"
          >
            {item.title}
            {item.external && <span className="sr-only">(opens in new tab)</span>}
          </a>
        )
      )}
    </nav>
  );
}

/** Mobile-only burger that exposes the top navigation links in a dropdown. */
function MobileTopNav() {
  const items = useVisibleTopNav();
  if (items.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden" aria-label="Open menu">
          <MenuIcon className="size-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel>Menu</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.map((item) =>
          item.to ? (
            <DropdownMenuItem key={item.title} asChild>
              <NavLink to={item.to} end={item.to === '/dashboard'} className="flex items-center gap-2">
                <item.icon aria-hidden className="size-4 shrink-0" />
                {item.title}
              </NavLink>
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem key={item.title} asChild>
              <a
                href={item.href}
                target={item.external ? '_blank' : undefined}
                rel={item.external ? 'noreferrer' : undefined}
                className="flex items-center gap-2"
              >
                <item.icon aria-hidden className="size-4 shrink-0" />
                {item.title}
                {item.external && <span className="sr-only">(opens in new tab)</span>}
              </a>
            </DropdownMenuItem>
          )
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function Header({ onMobileSidebarOpen }: { onMobileSidebarOpen: () => void }) {
  const homePath = useHomePath();

  return (
    <header className="border-border/80 bg-background/90 supports-[backdrop-filter]:bg-background/75 z-40 flex h-16 shrink-0 items-center border-b shadow-sm backdrop-blur">
      {/* Brand rail — fixed width, independent of the sidebar collapse state */}
      <div className="border-sidebar-border/80 hidden h-full w-64 shrink-0 items-center border-r px-4 md:flex">
        <Link
          to={homePath}
          className="focus-visible:ring-ring flex items-center gap-3 rounded-xl px-1 py-1 focus-visible:ring-2 focus-visible:outline-none"
        >
          <BrandIdentity />
        </Link>
      </div>

      <div className="flex flex-1 items-center gap-2 px-4 md:gap-3 md:px-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={onMobileSidebarOpen}
          aria-label="Open sidebar"
          className="-ml-1 rounded-xl hover:bg-secondary md:hidden"
        >
          <PanelLeftOpenIcon className="size-5" />
        </Button>

        <Link
          to={homePath}
          className="focus-visible:ring-ring flex items-center gap-3 rounded-xl py-1 focus-visible:ring-2 focus-visible:outline-none md:hidden"
        >
          <BrandIdentity />
        </Link>

        <TopNav />

        <div className="ml-auto flex items-center gap-2">
          <MobileTopNav />
          <ProfileMenu />
        </div>
      </div>
    </header>
  );
}
